import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

// MUI
import Visibility from '@mui/icons-material/Visibility';

// common
import MuiLoading from 'common/components/MuiLoading';
import MuiTable from 'common/components/MuiTable';
import IconButtonTooltip from 'common/components/IconButtonTooltip';
import { PDF } from 'svg';
import { formatDateWithoutTimezone } from 'common/utils';
import { KIOS_STORAGE } from 'api/API_App';

// screen
import { useTabsData } from 'modules/suppliers/detail/context/useTabsData';
import PdfViewer from 'modules/suppliers/detail/components/modals/PdfViewer';
import SupplyContractDetails from 'modules/suppliers/detail/components/modals/details/SupplyContractDetails';
import { type ContractFromKios } from 'modules/suppliers/services/useSupplyContract';
import BackToSuppliersPageButton from 'modules/suppliers/detail/components/BackToSuppliersPageButton';
import { defaultPaginationParams } from 'modules/suppliers/detail/components/tab-panels/utils';

const SupplyContractPanel = () => {
  const [openModal, setOpenModal] = useState<{
    pdfViewer: boolean;
    pdfUrl: string | null;
    pdfFileName: string;
    details: {
      open: boolean;
      data: ContractFromKios | null;
    };
  }>({
    pdfViewer: false,
    pdfUrl: null,
    pdfFileName: '',
    details: {
      open: false,
      data: null,
    },
  });

  const { supplyContract } = useTabsData();

  const onSortingChange = () => {
    // Sorting logic can be implemented here if needed
  };

  const onPageChange = () => {};
  const onRowsPerPageChange = () => {};

  const params = defaultPaginationParams;

  const handleOpenDetailsModal = (data: ContractFromKios) => {
    setOpenModal((prev) => ({
      ...prev,
      details: {
        open: true,
        data,
      },
    }));
  };

  const handleCloseDetailsModal = () => {
    setOpenModal((prev) => ({
      ...prev,
      details: { open: false, data: null },
    }));
  };

  const handleOpenPdfModal = (contract: ContractFromKios) => {
    // Extract PDF path from contract data
    const pdfPath = (contract as any).pdfPath;

    if (pdfPath) {
      const pdfPaths = Array.isArray(pdfPath) ? pdfPath : [pdfPath];
      if (pdfPaths.length > 0) {
        const firstPdfPath = pdfPaths[0] as string;
        const fileName = firstPdfPath.split('/').pop() || contract.contractNumber + '.pdf';
        const trimStorage = firstPdfPath.replace(/\/storage/, '');
        const formattedSpace = trimStorage.replace(/\s/g, '%20');
        const pdfUrl = `${KIOS_STORAGE}/${formattedSpace}`;

        setOpenModal((prev) => ({
          ...prev,
          pdfViewer: true,
          pdfUrl,
          pdfFileName: fileName,
        }));
      }
    }
  };

  const handleClosePdfModal = () => {
    setOpenModal((prev) => ({
      ...prev,
      pdfViewer: false,
      pdfUrl: null,
      pdfFileName: '',
    }));
  };

  if (!supplyContract) return <MuiLoading />;

  return (
    <>
      <MuiTable<ContractFromKios>
        data={supplyContract || []}
        state={{ isLoading: false }}
        manualSorting={false}
        onSortingChange={onSortingChange}
        paginationProps={{
          count: supplyContract.length || 0,
          page: params.pageIndex,
          rowsPerPage: params.pageSize,
          onPageChange,
          onRowsPerPageChange,
        }}
        columns={[
          {
            accessorKey: 'rowNumber',
            header: 'STT',
            Header: () => 'STT',
            size: 60,
            Cell: ({ row }) => {
              const rowIndex = row.index + 1; // row.index is 0-based
              const offset = (params.pageIndex - 1) * params.pageSize;
              return offset + rowIndex;
            },
          },
          {
            accessorKey: 'contractNumber',
            header: 'contractNumber',
            Header: () => <FormattedMessage id="supplier.supplyContract.number" />,
            enableSorting: true,
          },
          {
            accessorKey: 'name',
            header: 'name',
            Header: () => <FormattedMessage id="supplier.supplyContract.name" />,
          },
          {
            accessorKey: 'publishedAt',
            header: 'publishedAt',
            Header: () => <FormattedMessage id="supplier.supplyContract.signedDate" />,
            Cell: ({ renderedCellValue }) => {
              if (!renderedCellValue) return '';
              return formatDateWithoutTimezone(renderedCellValue.toString());
            },
            enableSorting: true,
          },
          {
            accessorKey: 'expiredAt',
            header: 'expiredAt',
            Header: () => <FormattedMessage id="supplier.supplyContract.expirationDate" />,
            Cell: ({ renderedCellValue, row: { original } }) => {
              const noExpiration = original.isUnlimited;
              if (noExpiration) return <FormattedMessage id="noExpiration" />;

              if (!renderedCellValue) return '';
              return formatDateWithoutTimezone(renderedCellValue.toString());
            },
            enableSorting: true,
          },
        ]}
        enableRowActions
        renderRowActions={({ row: { original } }) => {
          const pdfDisabled = !original.hasPdf;

          return [
            <IconButtonTooltip
              tooltipProps={{ title: <FormattedMessage id="quickView" /> }}
              onClick={() => handleOpenDetailsModal(original)}
            >
              <Visibility />
            </IconButtonTooltip>,
            <IconButtonTooltip
              tooltipProps={{ title: <FormattedMessage id="showPdf" /> }}
              onClick={() => handleOpenPdfModal(original)}
              disabled={pdfDisabled}
            >
              <PDF />
            </IconButtonTooltip>,
          ];
        }}
      />

      <BackToSuppliersPageButton />

      {/* Modals */}
      {openModal.details.open && openModal.details.data && (
        <SupplyContractDetails
          open={openModal.details.open}
          onClose={handleCloseDetailsModal}
          item={openModal.details.data}
        />
      )}
      {openModal.pdfViewer && (
        <PdfViewer
          open={openModal.pdfViewer}
          onClose={handleClosePdfModal}
          pdfUrl={openModal.pdfUrl}
          title={openModal.pdfFileName || 'PDF Document'}
        />
      )}
    </>
  );
};

export default SupplyContractPanel;
